import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import constants from '../../utilities/constants';

const BLUE = '#023e84';

// Keys match SHIP_FROM_PRESETS in the backend shippingTools.js.
const PRESETS = [
  { value: 'SRQ', label: 'Skyway SRQ', address: constants.skyway_locations.FL.address },
  { value: 'DAL', label: 'Skyway DAL', address: constants.skyway_locations.DAL.address },
];

const EMPTY_FROM = {
  name: 'Skyway Classics', phone: '', addressLine1: '', cityLocality: '', stateProvince: '', postalCode: '', countryCode: 'US',
};

export const DEFAULT_SHIP_FROM = { mode: 'SRQ', custom: EMPTY_FROM };

export const shipFromIsValid = ({ mode, custom }) => mode !== 'custom' || !!(
  custom.name && custom.addressLine1 && custom.cityLocality && custom.stateProvince && custom.postalCode
);

// What the getRates / getShippingRatesBatch functions expect as `shipFrom`.
export const shipFromPayload = ({ mode, custom }) => (mode === 'custom' ? custom : mode);

export default function ShipFromPicker({ value, onChange, disabled }) {
  const { mode, custom } = value;
  const setField = (key, val) => onChange({ mode, custom: { ...custom, [key]: val } });
  const preset = PRESETS.find(p => p.value === mode);

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Ship From
      </Typography>
      <ToggleButtonGroup
        value={mode}
        exclusive
        disabled={disabled}
        onChange={(_, m) => { if (m) onChange({ mode: m, custom }); }}
        size="small"
        sx={{
          mb: 1,
          '& .MuiToggleButton-root': { fontSize: '0.8rem', px: 2, py: 0.75, textTransform: 'none' },
          '& .MuiToggleButton-root.Mui-selected': { bgcolor: alpha(BLUE, 0.08), color: BLUE, fontWeight: 700 },
        }}
      >
        {PRESETS.map(p => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>

      {preset ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{preset.address}</Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Sender Name" size="small" sx={{ flex: 2 }} disabled={disabled}
              value={custom.name} onChange={(e) => setField('name', e.target.value)} />
            <TextField label="Phone (optional)" size="small" sx={{ flex: 1 }} disabled={disabled}
              value={custom.phone} onChange={(e) => setField('phone', e.target.value)} />
          </Box>
          <TextField label="Street Address" size="small" fullWidth disabled={disabled}
            value={custom.addressLine1} onChange={(e) => setField('addressLine1', e.target.value)} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="City" size="small" sx={{ flex: 2 }} disabled={disabled}
              value={custom.cityLocality} onChange={(e) => setField('cityLocality', e.target.value)} />
            <TextField label="State" size="small" sx={{ flex: 1 }} inputProps={{ maxLength: 2 }} disabled={disabled}
              value={custom.stateProvince} onChange={(e) => setField('stateProvince', e.target.value.toUpperCase())} />
            <TextField label="ZIP" size="small" sx={{ flex: 1 }} inputProps={{ maxLength: 10 }} disabled={disabled}
              value={custom.postalCode} onChange={(e) => setField('postalCode', e.target.value)} />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
